import React, { useEffect, useState } from 'react';
import { getAllCronJobs, createCronJob, updateCronJob, toggleCronJob, deleteCronJob, reloadCronJobs, getCronExpressionHelp } from '../services/cronJobs';
import { getAllTemplates } from '../services/messageTemplates';

const CronJobs = () => {
  const [cronJobs, setCronJobs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState({
    job_name: '',
    job_type: 'unverified_reminder',
    description: '',
    is_enabled: false,
    cron_expression: '0 9 * * *',
    recipient_filter: {
      min_days_since_registration: 3
    },
    template_id: ''
  });

  useEffect(() => {
    fetchCronJobs();
    fetchTemplates();
  }, []);

  const fetchCronJobs = async () => {
    try {
      setLoading(true);
      const response = await getAllCronJobs();
      setCronJobs(response.cronJobs || response.data || []);
    } catch (error) {
      console.error('Failed to fetch cron jobs:', error);
      alert('Failed to load cron jobs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await getAllTemplates();
      const templateList = response.templates || response.data || [];
      setTemplates(templateList.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingJob) {
        await updateCronJob(editingJob.id, formData);
        alert('Cron job updated successfully!');
      } else {
        await createCronJob(formData);
        alert('Cron job created successfully!');
      }
      
      setShowModal(false);
      setEditingJob(null);
      resetForm();
      fetchCronJobs();
    } catch (error) {
      alert('Failed to save cron job: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({
      job_name: '',
      job_type: 'unverified_reminder',
      description: '',
      is_enabled: false,
      cron_expression: '0 9 * * *',
      recipient_filter: {
        min_days_since_registration: 3
      },
      template_id: ''
    });
  };

  const handleToggle = async (id) => {
    try {
      await toggleCronJob(id);
      fetchCronJobs();
    } catch (error) {
      alert('Failed to toggle cron job: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this cron job?')) {
      try {
        await deleteCronJob(id);
        fetchCronJobs();
      } catch (error) {
        alert('Failed to delete cron job: ' + error.message);
      }
    }
  };

  const handleReload = async () => {
    try {
      await reloadCronJobs();
      alert('Cron jobs reloaded successfully!');
      fetchCronJobs();
    } catch (error) {
      alert('Failed to reload cron jobs: ' + error.message);
    }
  };

  const openEditModal = (job) => {
    setEditingJob(job);
    setFormData({
      job_name: job.job_name,
      job_type: job.job_type,
      description: job.description || '',
      is_enabled: job.is_enabled,
      cron_expression: job.cron_expression,
      recipient_filter: job.recipient_filter || { min_days_since_registration: 3 },
      template_id: job.template_id || ''
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingJob(null);
    resetForm();
    setShowModal(true);
  };

  const cronExamples = [
    { expression: '*/5 * * * *', description: 'Every 5 minutes' },
    { expression: '0 9 * * *', description: 'Every day at 9 AM' },
    { expression: '0 10 * * 1', description: 'Every Monday at 10 AM' },
    { expression: '0 8 1 * *', description: '1st day of every month at 8 AM' },
    { expression: '0 18 * * 1-5', description: 'Every weekday at 6 PM' },
    { expression: '*/30 * * * *', description: 'Every 30 minutes' }
  ];

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="text-xl">Loading cron jobs...</div>
    </div>;
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">Automated Cron Jobs</h1>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowHelpModal(true)}
            className="bg-gray-600 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-gray-700 text-sm md:text-base"
          >
            Cron Help
          </button>
          <button
            onClick={handleReload}
            className="bg-green-600 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-green-700 text-sm md:text-base"
          >
            Reload Jobs
          </button>
          <button
            onClick={openCreateModal}
            className="bg-blue-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-blue-700 text-sm md:text-base"
          >
            + Create Cron Job
          </button>
        </div>
      </div>

      <div className="grid gap-4">
        {cronJobs.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No cron jobs found. Create your first automated job!</p>
          </div>
        ) : (
          cronJobs.map((job) => (
            <div key={job.id} className="bg-white border rounded-lg p-4 shadow-sm">
              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <h3 className="text-lg md:text-xl font-semibold">{job.job_name}</h3>
                  <p className="text-sm md:text-base text-gray-600 mt-1">{job.description}</p>
                  <div className="mt-2 space-y-1">
                    <p className="text-xs md:text-sm"><strong>Type:</strong> {job.job_type}</p>
                    <p className="text-xs md:text-sm"><strong>Schedule:</strong> {job.cron_expression}</p>
                    <p className="text-xs md:text-sm"><strong>Last Run:</strong> {job.last_run ? new Date(job.last_run).toLocaleString() : 'Never'}</p>
                    <p className="text-xs md:text-sm"><strong>Next Run:</strong> {job.next_run ? new Date(job.next_run).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div className="mt-2">
                    <span className={`inline-block px-3 py-1 rounded-full text-sm ${job.is_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {job.is_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                  <button
                    onClick={() => handleToggle(job.id)}
                    className={`px-3 md:px-4 py-2 rounded text-sm ${job.is_enabled ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'} hover:opacity-80`}
                  >
                    {job.is_enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => openEditModal(job)}
                    className="px-3 md:px-4 py-2 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(job.id)}
                    className="px-3 md:px-4 py-2 bg-red-100 text-red-800 rounded hover:bg-red-200 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {editingJob ? 'Edit Cron Job' : 'Create Cron Job'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Job Name</label>
                <input
                  type="text"
                  value={formData.job_name}
                  onChange={(e) => setFormData({ ...formData, job_name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Job Type</label>
                <select
                  value={formData.job_type}
                  onChange={(e) => setFormData({ ...formData, job_type: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                >
                  <option value="unverified_reminder">Unverified User Reminder</option>
                  <option value="subscription_reminder">Subscription Reminder</option>
                  <option value="scheduled_messages">Scheduled Messages Processor</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Cron Expression</label>
                <input
                  type="text"
                  value={formData.cron_expression}
                  onChange={(e) => setFormData({ ...formData, cron_expression: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="0 9 * * *"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Example: 0 9 * * * (Every day at 9 AM)</p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Template</label>
                <select
                  value={formData.template_id}
                  onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">-- Select Template --</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Minimum Days Since Registration</label>
                <input
                  type="number"
                  value={formData.recipient_filter.min_days_since_registration}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    recipient_filter: { 
                      ...formData.recipient_filter, 
                      min_days_since_registration: parseInt(e.target.value) 
                    } 
                  })}
                  className="w-full border rounded px-3 py-2"
                  min="1"
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_enabled}
                    onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="text-sm font-medium">Enable this job immediately</span>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingJob(null);
                  }}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingJob ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showHelpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Cron Expression Guide</h2>
            <div className="mb-4">
              <p className="text-gray-700 mb-4">
                Cron expressions consist of 5 fields: minute, hour, day of month, month, and day of week.
              </p>
              <div className="bg-gray-50 p-4 rounded mb-4">
                <code className="text-sm">* * * * *</code>
                <p className="text-xs text-gray-600 mt-2">| | | | |</p>
                <p className="text-xs text-gray-600">| | | | +--- Day of week (0-7, Sun=0 or 7)</p>
                <p className="text-xs text-gray-600">| | | +----- Month (1-12)</p>
                <p className="text-xs text-gray-600">| | +------- Day of month (1-31)</p>
                <p className="text-xs text-gray-600">| +--------- Hour (0-23)</p>
                <p className="text-xs text-gray-600">+----------- Minute (0-59)</p>
              </div>
            </div>
            <h3 className="font-semibold mb-2">Common Examples:</h3>
            <div className="space-y-2">
              {cronExamples.map((example, index) => (
                <div key={index} className="flex justify-between p-2 bg-gray-50 rounded">
                  <code className="text-sm">{example.expression}</code>
                  <span className="text-sm text-gray-600">{example.description}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowHelpModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CronJobs;
